import React, { createContext, useReducer, useEffect } from "react";
import authService from "../services/auth";

const AuthStateContext = createContext();
const AuthDispatchContext = createContext();

export { AuthStateContext, AuthDispatchContext };

const initialState = {
  user: null,
  token: localStorage.getItem("token") || null,
  loading: true,
  error: null,
};

function authReducer(state, action) {
  switch (action.type) {
    case "REHYDRATE":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
      };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case "AUTH_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "LOGOUT":
      return { user: null, token: null, loading: false, error: null };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "UPDATE_USER": {
      const updatedUser = { ...state.user, ...action.payload };
      // Persist certain user fields to localStorage for immediate availability
      if (state.token) {
        const storedUser = { ...updatedUser };
        localStorage.setItem("user", JSON.stringify(storedUser));
      }
      return { ...state, user: updatedUser };
    }
    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const result = await authService.getCurrentUser();
        if (result) {
          // Merge server data with any localStorage overrides for immediate fields
          const storedUser = localStorage.getItem("user");
          const localUserData = storedUser ? JSON.parse(storedUser) : {};
          const mergedUser = { ...localUserData, ...result.user };
          dispatch({
            type: "REHYDRATE",
            payload: { user: mergedUser, token: result.token },
          });
        } else {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      } catch (err) {
        console.error("Failed to rehydrate auth", err);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        dispatch({ type: "AUTH_ERROR", payload: "Session expired" });
      }
    };

    bootstrap();
  }, []);

  // Sync auth state across tabs: if token is removed in another tab, logout here too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "token") return;
      // token removed in another tab
      if (!e.newValue) {
        dispatch({ type: "LOGOUT" });
      } else {
        // token added/changed in another tab - try to rehydrate
        (async () => {
          try {
            const result = await authService.getCurrentUser();
            if (result) {
              dispatch({
                type: "REHYDRATE",
                payload: { user: result.user, token: result.token },
              });
            }
          } catch (err) {
            console.error("Failed to rehydrate auth from storage event", err);
          }
        })();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = async (email, password) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const { token, user } = await authService.login(email, password);
      dispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
      // Force refresh user data to get complete profile info
      setTimeout(async () => {
        try {
          const result = await authService.getCurrentUser();
          if (result) {
            dispatch({ type: "UPDATE_USER", payload: result.user });
          }
        } catch (err) {
          console.error("Failed to refresh user data after login:", err);
        }
      }, 100);
      return { ok: true };
    } catch (err) {
      const details = err?.response?.data?.details || [err.message];
      dispatch({ type: "AUTH_ERROR", payload: details });
      return { ok: false, error: details };
    }
  };

  const signup = async (email, password, passwordConfirm) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const { user, message } = await authService.signup(
        email,
        password,
        passwordConfirm
      );
      // Do NOT log in user after signup
      dispatch({ type: "SET_LOADING", payload: false });
      return { ok: true, user, message };
    } catch (err) {
      const details = err?.response?.data?.details || [err.message];
      dispatch({ type: "AUTH_ERROR", payload: details });
      return { ok: false, error: details };
    }
  };

  const loginWithToken = (token, user) => {
    localStorage.setItem("token", token);
    dispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error("Failed to refresh user data after login:", err);
      // ignore server errors on logout
    }
    // Clear all localStorage data
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("studyTimer");
    localStorage.removeItem("studyTimerInfo");
    localStorage.removeItem("myspaceState");
    dispatch({ type: "LOGOUT" });
  };

  const updateUser = (userData) => {
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider
        value={{ login, signup, logout, loginWithToken, updateUser }}
      >
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export default AuthProvider;
